import type { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, sampleTime, mergeMap, tap } from 'rxjs';
import { buffer, catchError, map } from 'rxjs/operators';

import { IntegrationsHttpClient } from '@recruitee/integrations-http';
import type { Hotspot } from '@recruitee/integrations-types';
import { Hotspots } from '@recruitee/integrations-types';
import { stableStringify } from '@recruitee/utils';

import { IntegrationElementsActions } from './elements.store';

const candidateIdRequiredMap = <const>{
  [Hotspots.OfferPipelineBadges]: true,
  [Hotspots.CandidateProfileOverview]: true,
  [Hotspots.CandidateProfileMore]: true,
  [Hotspots.CandidateProfileBadges]: true,
  [Hotspots.CandidatesTableBadges]: true,
};

@Injectable({ providedIn: 'root' })
export class IntegrationElementsEffects {
  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IntegrationElementsActions.fetchCollection),
      tap(action => this.trackAction(action)),
      buffer(
        this.actions$.pipe(ofType(IntegrationElementsActions.fetchCollection), sampleTime(100)),
      ),
      map(actions => {
        const hotspots = actions.flatMap(({ payload }) => payload);
        return [...new Set(hotspots.map(hotspot => stableStringify(hotspot)))].map(
          hotspotJson => <Hotspot>JSON.parse(hotspotJson),
        );
      }),
      mergeMap(hotspots =>
        this.integrationsHttpClient.loadElements(hotspots).pipe(
          map(payload => IntegrationElementsActions.fetchCollectionSuccess({ payload })),
          catchError((error: HttpErrorResponse) =>
            of(
              IntegrationElementsActions.fetchCollectionFailed({
                payload: { error, hotspots },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private integrationsHttpClient: IntegrationsHttpClient,
    private errorHandler: ErrorHandler,
  ) {}

  private trackAction(action: ReturnType<typeof IntegrationElementsActions.fetchCollection>) {
    const hotspots = Array.isArray(action.payload) ? action.payload : [action.payload];
    const hotspotsCandidateIdRequired = <
      Array<Extract<Hotspot, { name: keyof typeof candidateIdRequiredMap }>>
    >hotspots.filter(
      hotspot => !!(<Record<Hotspots, boolean>>candidateIdRequiredMap)[hotspot.name],
    );
    const candidateIdMissing = hotspotsCandidateIdRequired.some(
      hotspot => !hotspot.params.candidateId,
    );
    if (candidateIdMissing) {
      try {
        // eslint-disable-next-line no-console
        console.log(JSON.stringify(hotspotsCandidateIdRequired, null, 2));
        throw new Error('Missing candidateId');
      } catch (e) {
        this.errorHandler.handleError(e);
      }
    }
  }
}
